import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getStudents, getCourse } from '../../api/instructorApi';
import { toTitleCase } from '../../utils/format';

const AV_COLORS = ['#E8440A', '#F7C91B', '#1A7048', '#5B6ABF', '#D4882A', '#8A6FB0'];
function avColor(i) { return AV_COLORS[i % AV_COLORS.length]; }
function initials(name = '') { return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase(); }

export default function InstructorStudents() {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course,   setCourse]   = useState(null);
  const [students, setStudents] = useState([]);
  const [stats,    setStats]    = useState(null);

  useEffect(() => {
    getCourse(Number(courseId)).then(d => setCourse(d.course)).catch(console.error);
    getStudents(Number(courseId)).then(d => {
      setStudents(d.students);
      setStats(d.stats);
    }).catch(console.error);
  }, [courseId]);

  if (!course) return <div className="body" style={{ color: 'var(--muted)' }}>Loading…</div>;

  return (
    <>
      {/* ── Header ── */}
      <div className="ph">
        <div>
          <div className="bc">Instructor / Students</div>
          <div className="pt">Enrolled Students</div>
          <div className="ps">{course.title} · {students.length} students</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-o btn-sm" onClick={() => navigate(`/instructor/courses/${courseId}`)}>← Course</button>
        </div>
      </div>

      <div className="body">
        {/* Stats */}
        <div className="stats" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
          <div className="sc">
            <div className="sc-l">Total Students</div>
            <div className="sc-v">{stats?.total_students ?? '—'}</div>
          </div>
          <div className="sc">
            <div className="sc-l">Avg. Progress</div>
            <div className="sc-v">{stats ? `${stats.avg_progress}%` : '—'}</div>
            {stats && (
              <div className="sc-bar">
                <div className="sc-fill" style={{ width: `${stats.avg_progress}%`, background: 'var(--yellow)' }} />
              </div>
            )}
          </div>
          <div className="sc">
            <div className="sc-l">Assignments Submitted</div>
            <div className="sc-v">{stats?.assignments_submitted ?? '—'}</div>
            <div className="sc-s" style={{ color: 'var(--green)' }}>
              {stats ? `of ${stats.total_possible} total` : ''}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="ct">
            <span>Student Progress</span>
            <span className="b b-y">{course.title}</span>
          </div>
          {students.length === 0 ? (
            <div className="empty">
              <div className="empty-icon">◈</div>
              <div>No students enrolled yet</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Progress</th>
                    <th>Modules</th>
                    <th>Avg Quiz</th>
                    <th>Assignments</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, i) => {
                    const allModulesDone     = s.modules_total > 0 && s.modules_completed === s.modules_total;
                    const allAssignmentsDone = s.total_assignments === 0 || s.assignments_submitted === s.total_assignments;
                    const status = allModulesDone && allAssignmentsDone ? 'Completed'
                      : s.progress_percent > 0 || s.assignments_submitted > 0 ? 'In Progress' : 'Not Started';
                    const sCls = status === 'Completed' ? 'b-g'
                      : status === 'In Progress' ? 'b-o' : 'b-gr';
                    return (
                      <tr
                        key={s.student_id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/instructor/courses/${courseId}/students/${s.student_id}`)}
                      >
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                            <div style={{
                              width: 26, height: 26, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontFamily: "'Nunito Sans',sans-serif", fontSize: 9, fontWeight: 700,
                              background: avColor(i), color: i === 1 ? 'var(--black)' : 'white',
                              flexShrink: 0,
                            }}>
                              {initials(s.student_name)}
                            </div>
                            {toTitleCase(s.student_name)}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 60, height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${s.progress_percent}%`, height: '100%', borderRadius: 3, background: 'var(--yellow)' }} />
                            </div>
                            <span style={{ fontSize: 11.5 }}>{s.progress_percent}%</span>
                          </div>
                        </td>
                        <td style={{ fontSize: 13 }}>{s.modules_completed}/{s.modules_total}</td>
                        <td style={{ fontSize: 13 }}>
                          {s.avg_quiz_score !== null ? `${s.avg_quiz_score}%` : '—'}
                        </td>
                        <td style={{ fontSize: 13 }}>{s.assignments_submitted}/{s.total_assignments}</td>
                        <td><span className={`b ${sCls}`}>{status}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
